//
// # MessagePlatform
//
// Implements the Platform interface on top of a PlatformTransport.
// This bridges the rich Platform API to message-based transports
// like WebSocket or postMessage.
//
// MessagePlatform handles:
// - Request/response patterns (getPdk, getStack, getContent)
// - Routing incoming messages to Editor methods
// - Sending outgoing messages via PlatformTransport
//
// Web platform creates a PlatformTransport and wraps it in MessagePlatform.
//

import { Result, msgs, Requests, logger, LoadKind } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { model } from "GenAlphaModel";
import {
  Platform,
  PlatformError,
  SelectionInfo,
} from "EditorCore/src/platform";
import { PlatformTransport } from "./transport";
import type { Editor } from "./editor";

export class MessagePlatform implements Platform {
  readonly name: string;

  // Editor reference - set after construction via setEditor()
  private editor: Editor | null = null;

  // Request trackers for async request/response patterns
  private readonly pdkRequests: Requests<
    msgs.GetPdkRequest,
    msgs.GetPdkResponse
  >;
  private readonly stackRequests: Requests<
    msgs.GetStackRequest,
    msgs.GetStackResponse
  >;
  private readonly contentRequests: Requests<
    msgs.ContentRequest,
    msgs.ContentResponse
  >;

  constructor(private readonly transport: PlatformTransport, name?: string) {
    this.name = name ?? `MessagePlatform(${transport.name})`;

    // Initialize request trackers with sendMessage bound to transport
    const sender = {
      sendMessage: (msg: msgs.ToPlatform) => transport.send(msg),
    };
    this.pdkRequests = new Requests(sender, 30);
    this.stackRequests = new Requests(sender, 30);
    this.contentRequests = new Requests(sender, 60);

    // Set up message handler
    transport.onMessage(this.handleMessage);
  }

  // Set the Editor reference. Called after Editor construction.
  setEditor(editor: Editor): void {
    this.editor = editor;
  }

  //
  // ## Platform Data Access Methods
  //

  async getPdk(pdkId: string): Promise<Result<schema.pdk.PDK, PlatformError>> {
    const r = await this.pdkRequests.send({
      kind: msgs.Kind.GetPdkRequest,
      data: pdkId,
    });

    if (r.err) {
      return Result.Err({ why: "request-failed", data: r.val });
    }

    // Validate the response data
    const valResult = schema.validators.PDK.validator.validate(r.val.data);
    if (valResult.err) {
      return Result.Err({ why: "invalid-pdk-response", data: valResult.val });
    }

    return Result.Ok(valResult.val);
  }

  async getStack(
    pdkId: string,
    stackId: schema.StackId
  ): Promise<Result<schema.pdk.Stack, PlatformError>> {
    const r = await this.stackRequests.send({
      kind: msgs.Kind.GetStackRequest,
      data: { pdkId, stackId },
    });

    if (r.err) {
      return Result.Err({ why: "request-failed", data: r.val });
    }

    const valResult = model.layout.Stack.validator.validate(r.val.data);
    if (valResult.err) {
      return Result.Err({ why: "invalid-stack-response", data: valResult.val });
    }

    return Result.Ok(valResult.val);
  }

  async getContent(
    ident: model.Ident
  ): Promise<Result<model.ContentJson, PlatformError>> {
    const r = await this.contentRequests.send({
      kind: msgs.Kind.ContentRequest,
      ident,
    });

    if (r.err) {
      return Result.Err({ why: "request-failed", data: r.val });
    }

    const response = r.val;
    if (!response.success) {
      return Result.Err({ why: "content-not-found", data: response.error });
    }

    const valResult = model.ContentJson.validator.validate(response.content);
    if (valResult.err) {
      return Result.Err({
        why: "invalid-content-response",
        data: valResult.val,
      });
    }

    return Result.Ok(valResult.val);
  }

  //
  // ## Platform Notification Methods
  //

  notifyEditorUp(contentType?: msgs.ContentType): void {
    this.transport.send({ kind: msgs.Kind.EditorUp, contentType });
  }

  notifyUIChange(change: model.Change): void {
    this.transport.send({
      kind: msgs.Kind.UIChange,
      change,
    });
  }

  notifySelectionChanged(_sel: SelectionInfo | null): void {
    // The Properties Panel is an IDE-host feature; message-based hosts
    // (Web / VsCode) do not consume selection notifications, so this is a
    // no-op here. No message kind is defined for it.
  }

  notifyEditInstanceParameters(instanceId: string): void {
    this.transport.send({
      kind: msgs.Kind.EditInstanceParameters,
      instanceId,
    });
  }

  notifyAddDummyDevices(
    _instanceIds: number[],
    _instanceNames: string[]
  ): void {
    // Not supported in VSCode extension (single-user only)
  }

  notifyAddInstanceAtPosition(position: { x: number; y: number }): void {
    this.transport.send({
      kind: msgs.Kind.AddInstanceAtPosition,
      position,
    });
  }

  notifyAddPinAtPosition(position: { x: number; y: number }): void {
    this.transport.send({
      kind: msgs.Kind.AddPinAtPosition,
      position,
    });
  }

  notifyRenameInstance(instanceId: string): void {
    this.transport.send({
      kind: msgs.Kind.RenameInstance,
      instanceId,
    });
  }

  notifyRenamePin(pinId: number, currentName: string): void {
    this.transport.send({
      kind: msgs.Kind.RenamePin,
      pinId,
      currentName,
    });
  }

  notifyEditPinProperties(
    pinId: number,
    currentDir: schema.core.PortDir
  ): void {
    void pinId;
    void currentDir;
    // Pin properties dialog is only supported in the IDE environment.
    // The MessagePlatform (web/vscode) doesn't support this feature yet.
    this.log("[MessagePlatform] notifyEditPinProperties not supported");
  }

  notifyEditWireLabel(wireId: number, currentLabel: string): void {
    void wireId;
    void currentLabel;
    // Wire label editing dialog is only supported in the IDE environment.
    // The MessagePlatform (web/vscode) doesn't support this feature yet.
    this.log("[MessagePlatform] notifyEditWireLabel not supported");
  }

  notifyAddTextAnnotation(position: { x: number; y: number }): void {
    void position;
    // Text annotation dialog is only supported in the IDE environment.
    // The MessagePlatform (web/vscode) doesn't support this feature yet.
    this.log("[MessagePlatform] notifyAddTextAnnotation not supported");
  }

  notifyEditTextAnnotation(elementId: number, currentText: string): void {
    void elementId;
    void currentText;
    // Text annotation dialog is only supported in the IDE environment.
    // The MessagePlatform (web/vscode) doesn't support this feature yet.
    this.log("[MessagePlatform] notifyEditTextAnnotation not supported");
  }

  notifyGenerateFromSourceRequest(): void {
    this.transport.send({
      kind: msgs.Kind.GenerateFromSourceRequest,
    });
  }

  notifyOpenInstanceTarget(target: model.Ident): void {
    this.transport.send({
      kind: msgs.Kind.OpenInstanceTarget,
      target,
    });
  }

  notifyViewInDepot(target: model.Ident): void {
    this.transport.send({
      kind: msgs.Kind.ViewInDepot,
      target,
    });
  }

  // Symbol pin/label dialogs are handled by the IDE workspace, not message transport.
  notifyAddSymbolPin(_position: { x: number; y: number }): void {}
  notifyEditSymbolPin(_pinData: {
    portName: string;
    width: number;
    dir: schema.core.PortDir;
    labelPosition: string;
    id?: number;
  }): void {}
  // Layout pin dialogs are handled by the IDE workspace, not message transport.
  notifyAddLayoutPin(_position: { x: number; y: number }): void {}
  notifyEditLayoutPin(_pinData: {
    name: string;
    layer: number;
    pinType: schema.core.PinType | undefined;
    id: number;
  }): void {}
  notifyAddExprLabel(_position: { x: number; y: number }): void {}
  notifyEditExprLabel(labelData: {
    kind: schema.symbol.ExprKind;
    name?: string;
    paramName?: string;
    id: number;
  }): void {
    void labelData;
  }
  notifyOpenGraphicsCatalogue(): void {}

  notifyViewCurrentDesignInDepot(): void {
    // Not supported in message-based platforms
    // The current design ident is not available in this context
  }

  // Power routing dialog is handled by the IDE workspace, not message transport.
  notifyConfigurePowerRouting(): void {}

  // Cmd+Shift+L is handled at the host layer.
  notifyGenerateFromSource(): void {}

  // No-op in message-based platforms (VSCode) — hierarchy panel is IDE-only.
  notifyShowInHierarchy(_instanceName: string, _instanceId: number): void {}

  // No-op in message-based platforms (VSCode) — Save As dialog is IDE-only.
  notifySaveAsRequested(): void {}

  // No parent breadcrumb support in message-based platforms (VSCode).
  hasParentBreadcrumb(): boolean {
    return false;
  }
  notifyOpenParent(): void {}

  // Symbol <-> schematic navigation is handled by the IDE workspace, which owns
  // the tab/router state; these are no-ops in message-based platforms (VSCode).
  notifyOpenLinkedSchematic(): void {}
  notifyOpenLinkedSymbol(): void {}
  notifyOpenSchematicForInstance(
    _schematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef>,
    _pdkId?: string
  ): void {}

  sendMainContent(content: schema.Content): void {
    this.transport.send({
      kind: msgs.Kind.MainContent,
      content,
    });
  }

  sendMainContentResponse(uuid: string, content: schema.Content): void {
    this.transport.send({
      kind: msgs.Kind.MainContent,
      content,
    });
  }

  sendUpdateMainContentResponse(
    uuid: string,
    result: Result<void, unknown>
  ): void {
    if (result.err) {
      this.transport.send({
        kind: msgs.Kind.UpdateMainContentResponse,
        uuid,
        success: false,
        error: result.val,
      });
    } else {
      this.transport.send({
        kind: msgs.Kind.UpdateMainContentResponse,
        uuid,
        success: true,
      });
    }
  }

  sendRefreshMainContentResponse(
    uuid: string,
    result: Result<void, unknown>
  ): void {
    if (result.err) {
      this.transport.send({
        kind: msgs.Kind.RefreshMainContentResponse,
        uuid,
        success: false,
        error: result.val,
      });
    } else {
      this.transport.send({
        kind: msgs.Kind.RefreshMainContentResponse,
        uuid,
        success: true,
      });
    }
  }

  sendCaptureCanvasResponse(
    uuid: string,
    result: Result<string, string>
  ): void {
    if (result.err) {
      this.transport.send({
        kind: msgs.Kind.CaptureCanvasResponse,
        uuid,
        success: false,
        error: result.val,
      });
    } else {
      this.transport.send({
        kind: msgs.Kind.CaptureCanvasResponse,
        uuid,
        success: true,
        imageBase64: result.val,
      });
    }
  }

  log(body: unknown): void {
    logger.log(body);
    this.transport.send({ kind: msgs.Kind.Log, body });
  }

  warn(body: unknown): void {
    logger.warn(body);
    this.transport.send({ kind: msgs.Kind.Warning, body });
  }

  error(body: unknown): void {
    logger.error(body);
    this.transport.send({ kind: msgs.Kind.Error, body });
  }

  //
  // ## Message Handling
  //
  // Handle incoming messages from the transport and route to Editor.
  //

  private handleMessage = async (msg: msgs.ToEditor): Promise<void> => {
    const { kind } = msg;

    // Route responses to their request trackers
    switch (kind) {
      case msgs.Kind.GetPdkResponse:
        this.pdkRequests.respond(msg);
        return;
      case msgs.Kind.GetStackResponse:
        this.stackRequests.respond(msg);
        return;
      case msgs.Kind.ContentResponse:
        this.contentRequests.respond(msg);
        return;
    }

    // Route other messages to the Editor
    if (!this.editor) {
      logger.warn(
        `[MessagePlatform] Received message but no editor set: ${kind}`
      );
      return;
    }

    switch (kind) {
      case msgs.Kind.UpdateMainContentRequest:
        await this.handleUpdateMainContentRequest(msg);
        return;
      case msgs.Kind.RefreshMainContent:
        await this.handleRefreshMainContentRequest(msg);
        return;
      case msgs.Kind.GetMainContentRequest:
        this.handleGetMainContentRequest(msg);
        return;
      case msgs.Kind.CaptureCanvasRequest:
        this.handleCaptureCanvasRequest(msg);
        return;
      case msgs.Kind.New:
        this.editor.createNew(msg);
        return;
      case msgs.Kind.UpdateSettings:
        this.editor.updateSettings(msg.settings);
        return;
      case msgs.Kind.ProcessingState:
        this.editor.handleProcessingState(msg);
        return;
      case msgs.Kind.ParasiticsStatusUpdate:
        this.editor.handleParasiticsStatusUpdate(msg);
        return;
      case msgs.Kind.Log:
        logger.log(msg.body);
        return;
      case msgs.Kind.Warning:
        logger.warn(msg.body);
        return;
      case msgs.Kind.Error:
        logger.error(msg.body);
        return;
    }
  };

  private async handleUpdateMainContentRequest(
    msg: msgs.UpdateMainContentRequest
  ): Promise<void> {
    if (!this.editor) return;

    const result = await this.editor.loadMainContent(msg.content);
    if (result.err) {
      this.sendUpdateMainContentResponse(msg.uuid, Result.Err(result.val));
    } else {
      this.sendUpdateMainContentResponse(msg.uuid, Result.Ok(undefined));
    }
  }

  private async handleRefreshMainContentRequest(
    msg: msgs.RefreshMainContentRequest
  ): Promise<void> {
    if (!this.editor) return;

    const result = await this.editor.loadMainContent(
      msg.content,
      LoadKind.refresh,
      msg.savedIdents
    );
    if (result.err) {
      this.sendRefreshMainContentResponse(msg.uuid, Result.Err(result.val));
    } else {
      this.sendRefreshMainContentResponse(msg.uuid, Result.Ok(undefined));
    }
  }

  private handleGetMainContentRequest(msg: msgs.GetMainContentRequest): void {
    if (!this.editor) return;

    // Editor.sendMainContentResponse expects the full message and handles
    // the response internally. In the future, we should refactor Editor to
    // have a getMainContent() method that just returns the content.
    this.editor.sendMainContentResponse(msg);
  }

  private handleCaptureCanvasRequest(msg: msgs.CaptureCanvasRequest): void {
    if (!this.editor) return;

    const toDataURL = this.editor.canvas.toDataURL;
    if (!toDataURL) {
      this.sendCaptureCanvasResponse(
        msg.uuid,
        Result.Err("Canvas does not support toDataURL")
      );
      return;
    }

    const result = toDataURL();
    if (result.err) {
      this.sendCaptureCanvasResponse(msg.uuid, Result.Err(String(result.val)));
    } else {
      this.sendCaptureCanvasResponse(msg.uuid, Result.Ok(result.val));
    }
  }
}
